import { useQuery } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import { type ModelProfileResponse } from "../AgentThreadRecord";

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;

/**
 * Cache key for an org's model profiles; without `orgId`, the prefix that
 * matches every org's. Changing an org's model configuration invalidates it.
 */
export function modelProfilesQueryKey(orgId?: string) {
  return orgId === undefined ? ["modelProfiles"] : ["modelProfiles", orgId];
}

/**
 * Fetches the org's effective chat model profiles from the API; the
 * `is_default` one is the org's default. Cached for 15 minutes since profiles
 * change rarely; saving the org's model configuration invalidates it.
 *
 * Defaults to the caller's current org; pass `orgIdOverride` when acting on
 * another org (e.g. Roboto-admin org pages).
 */
export function useModelProfiles(orgIdOverride?: string) {
  const { agentThreadService } = useDomainServices();
  const orgId = useCurrentOrgId(orgIdOverride);

  return useQuery<ModelProfileResponse[]>({
    queryKey: modelProfilesQueryKey(orgId),
    queryFn: ({ signal }) =>
      agentThreadService.getModelProfiles({ abortSignal: signal, orgId }),
    staleTime: FIFTEEN_MINUTES_MS,
  });
}
